import { useEffect, useState } from "react";
import { publicApi } from "@/lib/api";
import { Play, Music, Anchor, Video, Lock } from "lucide-react";
import AdminLoginModal from "@/pages/AdminLoginModal";

const LOGO = "/assets/logo.png";

export default function PublicPage() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [openSongId, setOpenSongId] = useState(null);
  const [showLogin, setShowLogin] = useState(false);

  useEffect(() => {
    let mounted = true;
    publicApi
      .get()
      .then((d) => {
        if (mounted) setData(d);
      })
      .catch(() => {})
      .finally(() => mounted && setLoading(false));
    return () => {
      mounted = false;
    };
  }, []);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-white">
        <div data-testid="public-loading" className="text-[color:var(--rr-blue)] font-heading text-lg">
          Loading songs...
        </div>
      </div>
    );
  }

  const settings = data?.settings || {};
  const songs = data?.songs || [];
  const isOpen = settings.is_open !== false;

  return (
    <div data-testid="public-page" className="min-h-screen bg-white">
      {/* Top bar with admin login */}
      <div className="w-full flex justify-end px-4 pt-3 sm:pt-4 bg-[color:var(--rr-blue-light)]">
        <button
          data-testid="open-admin-login-btn"
          onClick={() => setShowLogin(true)}
          className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs sm:text-sm font-semibold text-[color:var(--rr-blue)] bg-white border border-[color:var(--rr-blue)]/20 hover:border-[color:var(--rr-blue)] hover:bg-[color:var(--rr-blue)] hover:text-white transition"
        >
          <Lock className="w-3.5 h-3.5" /> Admin login
        </button>
      </div>

      {/* Header */}
      <header className="relative overflow-hidden bg-[color:var(--rr-blue-light)]">
        <div className="max-w-2xl mx-auto px-5 pt-4 pb-14 sm:pt-6 sm:pb-20 text-center">
          <img
            data-testid="public-logo"
            src={LOGO}
            alt="Rolling River Day Camp"
            className="mx-auto h-24 sm:h-32 w-auto drop-shadow-sm"
          />
          <h1
            data-testid="public-heading"
            className="font-heading text-3xl sm:text-4xl lg:text-5xl mt-5 text-[color:var(--rr-blue)] font-bold leading-tight"
          >
            {settings.heading || "Rolling River Song Practice"}
          </h1>
          {isOpen && settings.intro && (
            <p
              data-testid="public-intro"
              className="mt-4 text-[15px] sm:text-base text-slate-700 max-w-xl mx-auto leading-relaxed whitespace-pre-line"
            >
              {settings.intro}
            </p>
          )}
        </div>
        <div className="rr-wave h-[60px] w-full absolute bottom-0 left-0" aria-hidden="true" />
      </header>

      {/* Song list or closed */}
      <main className="max-w-2xl mx-auto px-4 sm:px-5 py-8 sm:py-10">
        {!isOpen ? (
          <div
            data-testid="public-closed"
            className="rounded-2xl border-2 border-dashed border-[color:var(--rr-gold-dark)] bg-[color:var(--rr-sand)] p-8 sm:p-10 text-center"
          >
            <Anchor className="w-10 h-10 text-[color:var(--rr-blue)] mx-auto mb-3" />
            <p className="font-heading text-xl sm:text-2xl text-[color:var(--rr-blue)]">
              {settings.closed_message || "Song practice is taking a short break."}
            </p>
          </div>
        ) : songs.length === 0 ? (
          <div
            data-testid="public-empty"
            className="rounded-2xl border border-[color:var(--rr-blue-light)] bg-[color:var(--rr-cream)] p-8 text-center"
          >
            <Music className="w-9 h-9 text-[color:var(--rr-blue)] mx-auto mb-3" />
            <p className="text-slate-700">Songs will appear here soon. Check back in a bit.</p>
          </div>
        ) : (
          <ul data-testid="song-list" className="space-y-3 sm:space-y-4">
            {songs.map((s, idx) => (
              <SongCard
                key={s.id}
                song={s}
                index={idx}
                isOpen={openSongId === s.id}
                onToggle={() => setOpenSongId(openSongId === s.id ? null : s.id)}
              />
            ))}
          </ul>
        )}
      </main>

      <footer className="max-w-2xl mx-auto px-5 pb-10 pt-2 text-center">
        <p className="text-xs text-slate-400">Rolling River Day Camp, Est. 1992</p>
      </footer>
      {showLogin && <AdminLoginModal onClose={() => setShowLogin(false)} />}
    </div>
  );
}

function SongCard({ song, index, isOpen, onToggle }) {
  const isVideo = song.media_type === "video";
  return (
    <li
      data-testid={`song-item-${song.id}`}
      className="group rounded-2xl bg-white border border-[color:var(--rr-blue-light)] shadow-sm hover:shadow-md transition-shadow overflow-hidden"
    >
      <button
        data-testid={`song-toggle-${song.id}`}
        onClick={onToggle}
        className="w-full flex items-center gap-4 px-4 py-4 sm:px-5 sm:py-5 text-left"
        aria-expanded={isOpen}
      >
        <span
          className="flex-none w-11 h-11 sm:w-12 sm:h-12 rounded-full flex items-center justify-center font-heading text-lg font-semibold shadow-sm"
          style={{
            background: isOpen ? "var(--rr-blue)" : "var(--rr-gold)",
            color: isOpen ? "#fff" : "var(--rr-blue-dark)",
            transition: "background 0.2s ease",
          }}
        >
          {isOpen ? <Play className="w-5 h-5 fill-white" /> : String(index + 1).padStart(2, "0")}
        </span>
        <span className="flex-1 min-w-0">
          <span className="flex items-center gap-2">
            <span
              data-testid={`song-title-${song.id}`}
              className="font-heading text-base sm:text-lg font-semibold text-[color:var(--rr-blue)] truncate"
            >
              {song.title}
            </span>
            <span
              className={`flex-none inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-semibold uppercase tracking-wider ${
                isVideo
                  ? "bg-[color:var(--rr-gold)] text-[color:var(--rr-blue-dark)]"
                  : "bg-[color:var(--rr-blue-light)] text-[color:var(--rr-blue)]"
              }`}
            >
              {isVideo ? <Video className="w-3 h-3" /> : <Music className="w-3 h-3" />}
              {isVideo ? "Video" : "Song"}
            </span>
          </span>
          {song.note && (
            <span
              data-testid={`song-note-${song.id}`}
              className="block text-sm text-slate-600 mt-0.5 truncate"
            >
              {song.note}
            </span>
          )}
        </span>
        <span className="flex-none text-xs font-medium text-[color:var(--rr-blue)] hidden sm:inline">
          {isOpen ? "Close" : isVideo ? "Watch" : "Play"}
        </span>
      </button>
      {isOpen && (
        <div
          data-testid={`song-player-${song.id}`}
          className="px-3 pb-4 sm:px-5 sm:pb-5"
        >
          <div className="rounded-xl overflow-hidden bg-black border border-[color:var(--rr-blue-light)]">
            {isVideo ? (
              <div className="relative w-full" style={{ paddingTop: "56.25%" }}>
                <iframe
                  title={song.title}
                  src={`https://drive.google.com/file/d/${song.file_id}/preview`}
                  allow="autoplay; fullscreen"
                  allowFullScreen
                  className="absolute inset-0 w-full h-full"
                  loading="lazy"
                />
              </div>
            ) : (
              <iframe
                title={song.title}
                src={`https://drive.google.com/file/d/${song.file_id}/preview`}
                allow="autoplay"
                className="w-full h-[80px] bg-white"
                loading="lazy"
              />
            )}
          </div>
        </div>
      )}
    </li>
  );
}
